// webapp/src/lib/offlineSearchFallback.js
//
// Offline search fallback. When the exact-key cache miss is paired with a
// network error envelope, this module finds the prefetched "full list"
// response for the endpoint and filters it client-side. See
// docs/superpowers/specs/2026-05-06-offline-prefetch-design.md and
// docs/superpowers/specs/2026-05-06-offline-read-cache-design.md.
//
// We intentionally mirror the backend `searchFields` / `searchFieldsNum`
// shape (see backend/lib/class/{visitor,animal,product,drug,diagnose}.js)
// so an offline match returns the same record an online search would have.

// ---------------------------------------------------------------------------
// Per-endpoint search-field config — keyed by exact endpoint string.

// Custom matchers used by applyEqualityFilters when the body field doesn't
// map to a same-named scalar on the item. Mirrors the relevant backend
// query logic (see backend/app/routes/api/*/get-*.js).

function speciesIdMatcher(item, value) {
    const sids = item && item.speciesIds;
    // Empty / missing speciesIds means "applies to all species" — backend
    // returns these regardless of the speciesId filter (see
    // backend/app/routes/api/products/get-product.js:242).
    if (!Array.isArray(sids) || sids.length === 0) return true;
    const num = Number(value);
    return sids.some(e => e && (Number(e.speciesId) === num || String(e.speciesId) === String(value)));
}

const SEARCH_CONFIG = {
    '/api/visitors/': {
        stringFields: [
            'category', 'name', 'firstname', 'lastname',
            'cellphone', 'cellphone2', 'cellphone3',
            'email', 'address', 'place', 'postalcode', 'description',
        ],
        numericFields: ['id', 'visitor', 'visitorNumber'],
    },
    '/api/animals/': {
        stringFields: ['pedigreeName', 'name', 'chipId', 'insuranceId', 'passportId', 'nkkId'],
        numericFields: ['id', 'animalNumber', 'visitorNumber'],
    },
    '/api/products/': {
        stringFields: ['uuidv4', 'name', 'description', 'gtin', 'productNumber', 'reminderSmsText'],
        numericFields: ['id', 'price', 'vat', 'discount'],
        equalityMatchers: {
            speciesId: speciesIdMatcher,
        },
    },
    '/api/drugs/': {
        stringFields: ['uuidv4', 'name', 'producer', 'description'],
        numericFields: ['id', 'units'],
        equalityMatchers: {
            speciesId: speciesIdMatcher,
        },
    },
    '/api/diagnoses/': {
        stringFields: ['uuidv4', 'diagnoseCode', 'name', 'description'],
        numericFields: ['id'],
    },
};

export function getSearchConfig(endpoint) {
    return SEARCH_CONFIG[endpoint] || null;
}

// ---------------------------------------------------------------------------
// Client-side filter — mirrors backend searchType:'and'. Each whitespace
// token must hit at least one searchField (case-insensitive substring) or
// match a numeric field exactly.

function normalize(value) {
    if (value == null) return '';
    return String(value).toLowerCase();
}

export function matchesSearch(item, tokens, config) {
    if (!tokens.length) return true;
    const stringValues = config.stringFields.map(f => normalize(item[f]));
    const numericValues = config.numericFields.map(f => item[f]);
    for (let i = 0; i < tokens.length; i += 1) {
        const token = tokens[i];
        const tokenLower = token.toLowerCase();
        const num = parseInt(token, 10);
        const isNumeric = !isNaN(num) && String(num) === token;
        let hit = false;
        for (let j = 0; j < stringValues.length; j += 1) {
            if (stringValues[j].indexOf(tokenLower) !== -1) {
                hit = true;
                break;
            }
        }
        if (!hit && isNumeric) {
            for (let j = 0; j < numericValues.length; j += 1) {
                if (numericValues[j] === num) {
                    hit = true;
                    break;
                }
            }
        }
        if (!hit) return false;
    }
    return true;
}

export function clientSideFilter(items, searchTerm, endpoint) {
    if (!Array.isArray(items)) return [];
    const config = getSearchConfig(endpoint);
    if (!config) return items;
    const tokens = (searchTerm || '').split(/\s+/).filter(t => t.trim() !== '');
    if (!tokens.length) return items;
    return items.filter(item => matchesSearch(item, tokens, config));
}

// ---------------------------------------------------------------------------
// Find the prefetched "full list" entry for an endpoint+user+customer. We
// pick the entry with the largest data array — by construction the prefetch
// run produces entries with limit:1000-2000 records, much bigger than any
// search-result response that may also be cached.

function isFreshEntry(entry, ttlMs) {
    if (!entry || typeof entry.savedAt !== 'number') return false;
    return (Date.now() - entry.savedAt) < ttlMs;
}

function dataLength(entry) {
    const data = entry && entry.response && entry.response.data;
    return Array.isArray(data) ? data.length : 0;
}

export async function findPrefetchFallback(storage, endpoint, userId, customerId, ttlMs) {
    if (!storage || !storage.getAllMatching) return null;
    const u = userId == null ? null : String(userId);
    const c = customerId == null ? null : String(customerId);
    const matches = await storage.getAllMatching((value) => {
        if (!value || value.endpoint !== endpoint) return false;
        if (!isFreshEntry(value, ttlMs)) return false;
        const eu = value.userId == null ? null : String(value.userId);
        const ec = value.customerId == null ? null : String(value.customerId);
        if (u != null && eu !== u) return false;
        if (c != null && ec !== c) return false;
        return true;
    });
    if (!matches.length) return null;
    matches.sort((a, b) => dataLength(b.value) - dataLength(a.value));
    const best = matches[0];
    return best && dataLength(best.value) > 0 ? best.value : null;
}

// ---------------------------------------------------------------------------
// Body fields that control the request shape rather than identifying records.
// We skip these when applying equality filters so e.g. `?id=197277` narrows
// the result set but `?limit=100` does not.

const CONTROL_FIELDS = new Set([
    'search', 'limit', 'offset', 'sort', 'addData', 'extendedView',
    'customer', 'filter', 'skipFilter', 'useTextIndexCombined', 'searchType',
    'version', 'window', 'event',
]);

function looseEquals(itemValue, bodyValue) {
    if (itemValue === bodyValue) return true;
    if (itemValue == null || bodyValue == null) return false;
    // Numeric compare — handles "197277" vs 197277.
    const a = Number(itemValue);
    const b = Number(bodyValue);
    if (!isNaN(a) && !isNaN(b) && a === b) return true;
    return String(itemValue) === String(bodyValue);
}

function applyEqualityFilters(items, body, endpoint) {
    if (!body) return items;
    const filterKeys = Object.keys(body).filter(k => !CONTROL_FIELDS.has(k));
    if (!filterKeys.length) return items;
    const config = endpoint ? getSearchConfig(endpoint) : null;
    const matchers = (config && config.equalityMatchers) || {};
    return items.filter(item => filterKeys.every((k) => {
        const bv = body[k];
        const customMatcher = matchers[k];
        if (customMatcher) {
            if (Array.isArray(bv)) return bv.some(v => customMatcher(item, v));
            return customMatcher(item, bv);
        }
        if (Array.isArray(bv)) {
            return bv.some(v => looseEquals(item[k], v));
        }
        return looseEquals(item[k], bv);
    }));
}

// ---------------------------------------------------------------------------
// Build a synthetic response envelope from a prefetched entry, applying the
// caller's `search`, equality filters (id, owner, ...), and `limit` body
// params. Returns null if the endpoint has no search-config registered
// (unknown endpoint, can't filter safely).

export function buildFallbackResponse(prefetchEntry, endpoint, body) {
    if (!prefetchEntry || !prefetchEntry.response) return null;
    if (!getSearchConfig(endpoint)) return null;
    const sourceData = Array.isArray(prefetchEntry.response.data) ? prefetchEntry.response.data : [];
    const search = body && typeof body.search === 'string' ? body.search : '';
    let filtered = search ? clientSideFilter(sourceData, search, endpoint) : sourceData;
    filtered = applyEqualityFilters(filtered, body, endpoint);
    const limit = body && Number.isFinite(body.limit) ? body.limit : null;
    if (limit && filtered.length > limit) {
        filtered = filtered.slice(0, limit);
    }
    return Object.assign({}, prefetchEntry.response, {
        data: filtered,
        total: filtered.length,
        _fromCache: true,
        _fromPrefetchFallback: true,
    });
}
